import React from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import { CircularProgress, LinearProgress } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import {
  API_URL_VALIDATION,
  API_URL_VALIDATION_STATUS,
  API_URL_POST_LANES,
} from "../../configuration/Configuration";

export const getValidationIssueCounts = (validationData) => {
  if (!validationData || validationData.length === 0) {
    return { blocking: 0, informational: 0 };
  }

  const criticalPatterns = [
    "MISSING COLUMN",
    "CRITICAL ERROR:",
    "ERROR: Not a valid country",
    "address validation failed",
    "ZIP code mismatch",
  ];
  const nonCriticalPatterns = [
    "INFO:",
    "NO MATCHING POSSIBLE:",
    "No matching possible",
  ];

  return validationData.reduce(
    (counts, finding) => {
      const text = String(finding || "");
      if (text.trim() === "" || text.includes("NONE")) {
        return counts;
      }

      if (criticalPatterns.some((pattern) => text.includes(pattern))) {
        return { ...counts, blocking: counts.blocking + 1 };
      }

      if (nonCriticalPatterns.some((pattern) => text.includes(pattern))) {
        return { ...counts, informational: counts.informational + 1 };
      }

      return { ...counts, blocking: counts.blocking + 1 };
    },
    { blocking: 0, informational: 0 }
  );
};

export const checkValidationResults = (validationData) => {
  return getValidationIssueCounts(validationData).blocking === 0;
};

const Validation = (props) => {
  const [lanesData, setLanesData] = React.useState({});
  const [fileData, setFileData] = React.useState([""]);
  const [isPosting, setIsPosting] = React.useState(false);
  const [isValidating, setIsValidating] = React.useState(false);

  const [validateState, setValidateState] = React.useState(false);
  const [validationPassed, setValidationPassed] = React.useState(false);
  const [isAsync, setIsAsync] = React.useState(false);
  const [validationProgress, setValidationProgress] = React.useState("");
  const [progressPercent, setProgressPercent] = React.useState(0);

  React.useEffect(() => {
    window.addEventListener("beforeunload", handleUnload);

    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  // reset states before component leave
  const handleUnload = () => {
    setValidateState(false);
    setValidationPassed(false);
    setFileData([""]);
    setLanesData({});
    setIsValidating(false);
  };

  const isValidated = (state) => {
    return state;
  };

  const pollValidationStatus = async (taskId, maxAttempts = 150) => {
    // Poll every 2 seconds, max 5 minutes (150 attempts)
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const res = await axios.get(`${API_URL_VALIDATION_STATUS}${taskId}`);
        const status = res.data;

        console.log("Validation status:", status); // Debug log

        if (status.state === "SUCCESS") {
          setValidationProgress("");
          setProgressPercent(0);
          setFileData(status.results);
          const validationPassedResult = checkValidationResults(status.results);
          setValidationPassed(validationPassedResult);
          setValidateState(true);
          setIsValidating(false);
          return true;
        } else if (status.state === "FAILURE") {
          setValidationProgress("");
          setProgressPercent(0);
          setIsValidating(false);
          alert(
            `Validation failed: ${status.error || "Unknown error occurred"}`
          );
          return false;
        } else if (status.state === "PROGRESS") {
          // Extract detailed progress information
          const info = status.info || {};
          const progress = info.progress || 0;
          const stage = info.stage || "Validating...";
          
          setProgressPercent(progress);
          setValidationProgress(stage);
        } else if (status.state === "PENDING" || status.state === "STARTED") {
          setValidationProgress("Starting validation...");
          setProgressPercent(0);
        } else {
          setValidationProgress(`Validation in progress (${status.state})...`);
        }

        // Wait 2 seconds before next poll
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } catch (error) {
        console.error("Error checking validation status:", error);
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    // Timeout after max attempts
    setValidationProgress("");
    setProgressPercent(0);
    setIsValidating(false);
    alert(
      "Validation timeout: The validation is taking longer than expected. Please try again or reduce your dataset size."
    );
    return false;
  };

  const handleValidate = async () => {
    setIsValidating(true);
    setValidationProgress("");
    try {
      lanesData["data"] = props.mapData;
      lanesData["tenderID"] = props.tender;
      const res = await axios.post(API_URL_VALIDATION, lanesData);

      // Check if response is async or sync
      if (res.data.async) {
        // Async validation - start polling
        setIsAsync(true);
        const rowCount = props.mapData?.rows?.length || 0;
        setValidationProgress(
          `Large dataset detected (${rowCount} rows). Validation running in background...`
        );
        await pollValidationStatus(res.data.task_id);
      } else {
        // Sync validation - results ready immediately
        setIsAsync(false);
        setFileData(res.data.results);
        const validationPassedResult = checkValidationResults(res.data.results);
        setValidationPassed(validationPassedResult);
        setValidateState(true);
        setIsValidating(false);
      }
    } catch (error) {
      console.error("Validation error:", error);
      setIsValidating(false);
      setValidationProgress("");
      alert("An error occurred during validation. Please try again.");
    }
  };

  const handlePosting = async () => {

    setIsPosting(true);
    lanesData["tenderID"] = props.tender;
    lanesData["validationPassed"] = validationPassed;
    props.func(true);

    try {
      await axios.post(API_URL_POST_LANES, lanesData);
      setIsPosting(false);
      props.handleNext();
    } catch (error) {
      setIsPosting(false);
      if (error.response && error.response.status === 400) {
        alert(
          "Save failed: " +
            (error.response.data.message ||
              "Validation errors prevent saving this tender.")
        );
      } else {
        alert("An error occurred while saving the tender.");
      }
    }
  };

  const validationIssueCounts = getValidationIssueCounts(fileData);

  return (
    <Grid container spacing={2} justify="flex-end" alignItems="center">
      <Grid item xs={5.5}>
        <Typography align="left" sx={{ mt: 2, mb: 1 }}>
          <strong>
            Check if all the provided information can be processed.
          </strong>
        </Typography>
        <Typography align="left" sx={{ mt: 2, mb: 1 }}>
          <strong>
            {" "}
            Please SAVE the lanes before finishing the new tender process.
          </strong>
        </Typography>
        {validationProgress && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
              align="left"
              sx={{ color: "#1976d2", fontSize: 14, mb: 1 }}
            >
              <CircularProgress size={14} sx={{ mr: 1 }} />
              {validationProgress}
            </Typography>
            {progressPercent > 0 && (
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{ height: 8, borderRadius: 4 }}
              />
            )}
          </Box>
        )}
      </Grid>

      <Grid item xs={12}>
        {isValidated(validateState) && (
          <Box
            component="nav"
            sx={{ border: 1, borderRadius: "5px", padding: "16px", paddingTop:0 }}
          >
            <Typography
              sx={{
                mt: 2,
                textAlign: "left",
                fontSize: 14,
                color: validationPassed ? "#2e7d32" : "#a2232b",
              }}
            >
              {validationPassed
                ? validationIssueCounts.informational === 0
                  ? "Validation passed! No critical issues found."
                  : `No critical issues found. ${validationIssueCounts.informational} warnings/info findings need review.`
                : validationIssueCounts.blocking > 0
                ? `Validation failed! ${validationIssueCounts.blocking} critical issues found.`
                : "Validation failed! Critical issues found."}
            </Typography>
            {
              <List>
                {fileData.map((text, index) => (
                  <ListItem key={index} disablePadding>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color:
                          text.includes("CRITICAL ERROR") ||
                          text.includes("ERROR:") ||
                          text.includes("MISSING COLUMN") ||
                          text.includes("validation failed")
                            ? "#d32f2f"
                            : text.includes("NO MATCHING POSSIBLE:")
                            ? "#ed6c02"
                            : text.includes("No matching possible")
                            ? "#e65100"
                            : text.includes("INFO")
                            ? "#1976d2"
                            : "inherit",
                        fontWeight:
                          text.includes("NO MATCHING POSSIBLE:") ? 600 : "inherit",
                      }}
                    >
                      {text}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            }
          </Box>
        )}
      </Grid>
      <Grid item xs={4}></Grid>

      <Grid item xs={2}>
        <Box sx={{ flex: "1 1 auto" }} />
        <Button
          color="inherit"
          sx={{ width: "80%" }}
          onClick={handleValidate}
          name="validate"
          variant="outlined"
          size="large"
          disabled={isValidating}
          startIcon={isValidating ? <CircularProgress size={16} /> : null}
        >
          {isValidating ? "Validating..." : "Validate"}
        </Button>
      </Grid>
      <Grid item xs={2}>
        <Box sx={{ flex: "1 1 auto" }} />
        {isValidated(validateState) ? (
          <Button
            sx={{ width: "80%" }}
            onClick={handlePosting}
            variant="contained"
            size="large"
            color={validationPassed ? "primary" : "warning"}
            title={
              validationPassed
                ? "Save validated lanes"
                : "Save despite validation errors"
            }
          >
            {validationPassed ? "Save" : "Save Anyway"}
          </Button>
        ) : (
          <Button
            disabled
            color="inherit"
            sx={{ width: "80%" }}
            variant="outlined"
            size="large"
          >
            Save
          </Button>
        )}
      </Grid>

      <Grid item xs={3}></Grid>
      <Grid item xs={12} sx={{ mt: 3 }}>
        {isPosting && <CircularProgress></CircularProgress>}
      </Grid>
      <Grid item xs={4}></Grid>
      <Grid item xs={4}></Grid>

      <Grid item xs={4}></Grid>
    </Grid>
  );
};
export default Validation;
